import { useState, useRef, useEffect } from 'react';
import { Wrapper, ButtonSection, SectionTitle, ButtonGrid, ActionButton } from "../style-comps/AdminAttention.styles";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
    deleteAllUsers,
    deleteUserListings,
    deleteListings,
    deleteReservations,
    initUsers,
    initListings,
    initUserListings,
    initReservations,
    debugHostifyIds,
    syncAllListings,
    importListingPlatformUrls,
    getSyncJobById,
    getSyncCurrentJob,
    debugMissingUsers,
    debugMissingListings,
    debugMissingReservations,
    debugMissingUserListings
} from "../services/api";
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import authService from '../services/authService';

const MySwal = withReactContent(Swal);

const debugActions = [
    {
        label: "Debug Hostify IDs",
        handler: debugHostifyIds,
        type: "debug",
        message: "這將獲取並顯示所有使用者的 Hostify IDs，確定要執行嗎？"
    },
    {
        label: "檢查遺漏的 Hostify 使用者",
        handler: debugMissingUsers,
        type: "debug",
        message: "將檢查遺漏的 Hostify 使用者資料，確定要執行嗎？"
    },
    {
        label: "檢查遺漏的 Hostify 房源",
        handler: debugMissingListings,
        type: "debug",
        message: "將檢查遺漏的 Hostify 房源資料，確定要執行嗎？"
    },
    {
        label: "檢查遺漏的 Hostify 訂單",
        handler: debugMissingReservations,
        type: "debug",
        message: "將檢查遺漏的 Hostify 訂單資料，確定要執行嗎？"
    },
    {
        label: "檢查遺漏的 UserListing 關聯",
        handler: debugMissingUserListings,
        type: "debug",
        message: "將檢查遺漏的 UserListing 關聯資料，確定要執行嗎？"
    }
];


const deleteActions = [
    {
        label: "清除所有使用者資料",
        handler: deleteAllUsers,
        type: "danger",
        message: "這將永久刪除所有 User 資料與 UserRole，確定執行嗎？"
    },
    {
        label: "清除 UserListings",
        handler: deleteUserListings,
        type: "danger",
        message: "這將刪除所有 UserListing，但不會影響 Listing，確定執行嗎？"
    },
    {
        label: "清除 Listings 資料",
        handler: deleteListings,
        type: "danger",
        message: "這將刪除所有房源及其關聯資料，確定執行嗎？"
    },
    {
        label: "清除所有訂單",
        handler: deleteReservations,
        type: "danger",
        message: "這將刪除所有訂單資料，確定執行嗎？"
    }
];

const initActions = [
    {
        label: "初始化使用者",
        handler: initUsers,
        type: "init",
        message: "將重新從 Hostify 初始化所有使用者。確定要執行嗎？"
    },
    {
        label: "重建 UserListings",
        handler: initUserListings,
        type: "init",
        message: "將根據 parent listing 重建 UserListings。確定嗎？"
    },
    {
        label: "初始化 Listings",
        handler: initListings,
        type: "init",
        message: "這將初始化所有房源並重建 UserListings，確定嗎？"
    },
    {
        label: "初始化訂單資料",
        handler: initReservations,
        type: "init",
        message: "這會完整同步所有 Hostify 訂單資料，確定要執行嗎？"
    }
];

const AdminAttention = () => {
    // 所有 Hooks 必須在組件頂層調用
    const permissions = usePermissions();

    // 強制重新載入 authService
    console.log('強制重新載入 authService...');

    // 手動觸發 authService 重新載入
    authService.restoreSession();

    // 取得當前用戶資料
    const currentUser = authService.getCurrentUser();
    console.log('當前用戶資料:', currentUser);

    // 多重權限檢查策略
    let hasAccess = false;
    let accessMethod = '';

    // 檢查 1: 手動從 localStorage 載入權限
    const authData = localStorage.getItem('authData');
    if (authData) {
        try {
            const userData = JSON.parse(authData);
            console.log('🔧 手動載入的用戶資料:', userData);

            const isAdminByRole = userData.roles?.includes('Admin');
            const isOwnerByRole = userData.roles?.includes('Owner');
            const hasSystemAccess = userData.permissions && userData.permissions['System.FullAccess']?.HasPermission;

            console.log('手動權限檢查:');
            console.log('   Admin 角色:', isAdminByRole);
            console.log('   Owner 角色:', isOwnerByRole);
            console.log('   System.FullAccess:', hasSystemAccess);

            if (isAdminByRole || isOwnerByRole || hasSystemAccess) {
                hasAccess = true;
                accessMethod = '手動權限檢查';
                console.log('手動權限檢查通過');
            }
        } catch (error) {
            console.error('❌ 解析 authData 失敗:', error);
        }
    }

    // 檢查 2: 舊系統角色檢查
    if (!hasAccess) {
        const userRole = JSON.parse(localStorage.getItem('roles') || '[]')[0] || '';
        const legacyAdmin = userRole === 'Admin' || userRole === 'Owner';

        console.log('舊系統檢查 - userRole:', userRole, 'legacyAdmin:', legacyAdmin);

        if (legacyAdmin) {
            hasAccess = true;
            accessMethod = '舊系統角色檢查';
            console.log('舊系統權限檢查通過');
        }
    }

    // 檢查 3: usePermissions Hook (如果其他檢查都失敗)
    if (!hasAccess) {
        try {
            const systemAccess = permissions.hasPermission(PERMISSIONS.SYSTEM_FULL_ACCESS);
            const adminRole = permissions.hasRole('Admin');
            const ownerRole = permissions.hasRole('Owner');

            console.log('usePermissions 檢查:');
            console.log('   systemAccess:', systemAccess);
            console.log('   adminRole:', adminRole);
            console.log('   ownerRole:', ownerRole);

            if (systemAccess || adminRole || ownerRole) {
                hasAccess = true;
                accessMethod = 'usePermissions Hook';
                console.log('usePermissions 權限檢查通過');
            }
        } catch (error) {
            console.error('usePermissions 檢查失敗:', error);
        }
    }

    // 🚨 處理動作的共用函數
    const handleAction = async (action) => {
        const result = await MySwal.fire({
            title: "請再次確認",
            text: action.message,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#61cbc9",
            cancelButtonColor: "#d33",
            confirmButtonText: "確定",
            cancelButtonText: "取消"
        });

        if (result.isConfirmed) {
            try {
                await action.handler();
                MySwal.fire("成功", "操作已完成。", "success");
            } catch (error) {
                const msg = error?.response?.data?.message || error.message;
                MySwal.fire("錯誤", msg, "error");
            }
        }
    };

    // 📊 調試功能（僅限非 production 環境，且不印出含 token 的完整 currentUser）
    const handleDebug = () => {
        if (process.env.NODE_ENV === 'production') return;
        console.log('=== 手動調試資訊 ===');

        console.log('roles:', localStorage.getItem('roles'));
        console.log('currentUser:', authService.getCurrentUser()?.userName);
        authService.debugPermissions();
    };

    // 匯入 PlatformUrls
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const handleImportPlatformUrls = async () => {
        if (!importFile) {
            MySwal.fire("請選擇檔案", "請先選擇一個 .xlsx 檔案", "warning");
            return;
        }

        const result = await MySwal.fire({
            title: "請再次確認",
            text: "將從上傳的 xlsx 匯入 Listing PlatformUrls，確定執行嗎？",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#61cbc9",
            cancelButtonColor: "#d33",
            confirmButtonText: "確定",
            cancelButtonText: "取消"
        });

        if (!result.isConfirmed) return;

        try {
            setImporting(true);
            await importListingPlatformUrls(importFile);
            MySwal.fire("成功", "PlatformUrls 匯入完成。", "success");
            setImportFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            const msg = error?.response?.data?.message || error.message;
            MySwal.fire("錯誤", msg, "error");
        } finally {
            setImporting(false);
        }
    };

    //  Sync-All 狀態追蹤
    const [syncJobId, setSyncJobId] = useState(() => localStorage.getItem('syncJobId') || null);
    const [inputJobId, setInputJobId] = useState('');
    const [syncStatus, setSyncStatus] = useState(() => localStorage.getItem('syncStatus') || null);
    const [syncDetail, setSyncDetail] = useState(() => {
        const stored = localStorage.getItem('syncDetail');
        return stored ? JSON.parse(stored) : null;
    });
    const syncPollingRef = useRef(null);
    const stopPolling = () => {
        if (syncPollingRef.current) {
            clearInterval(syncPollingRef.current);
            syncPollingRef.current = null;
        }
    };
    const startPolling = (jobId) => {
        stopPolling();
        const intervalId = setInterval(async () => {
            try {
                const data = await getSyncJobById(jobId);
                const status = data?.Status?.toLowerCase();
                setSyncDetail(data);
                setSyncStatus(status);
                localStorage.setItem('syncDetail', JSON.stringify(data));
                localStorage.setItem('syncStatus', status);
                if (status === 'completed' || status === 'failed') {
                    clearInterval(intervalId);
                    syncPollingRef.current = null;
                }
            } catch (err) {
                console.error('❌ 輪詢 sync job 失敗:', err);
                clearInterval(intervalId);
                syncPollingRef.current = null;
            }
        }, 3000);
        syncPollingRef.current = intervalId;
    };
    // 頁面載入時恢復 polling
    // 頁面載入時恢復 polling — 先驗後端狀態再決定要不要繼續
    useEffect(() => {
        const storedJobId = localStorage.getItem('syncJobId');
        const storedStatus = localStorage.getItem('syncStatus');

        if (!storedJobId || storedStatus !== 'running') {
            return () => stopPolling();
        }

        // 有 jobId 且狀態是 running → 先問後端現在實際狀態
        (async () => {
            try {
                const data = await getSyncJobById(storedJobId);
                const status = data?.Status?.toLowerCase();

                if (status === 'running') {
                    // 真的還在跑 → 繼續 polling
                    setSyncDetail(data);
                    setSyncStatus('running');
                    startPolling(storedJobId);
                } else {
                    // 已結束（completed / failed / 其他）→ 更新狀態、清 localStorage
                    setSyncDetail(data);
                    setSyncStatus(status);
                    localStorage.setItem('syncStatus', status);
                    localStorage.setItem('syncDetail', JSON.stringify(data));
                    // jobId 保留讓 UI 還能顯示 job 號碼，只是不再 polling
                }
            } catch {
                // API 打不到 → 清掉舊狀態，不要卡在 running
                setSyncStatus(null);
                setSyncDetail(null);
                setSyncJobId(null);
                localStorage.removeItem('syncJobId');
                localStorage.removeItem('syncStatus');
                localStorage.removeItem('syncDetail');
            }
        })();

        return () => stopPolling();
    }, []);



    // 覆寫 SyncActions 的 handler，改用這個
    const handleSyncAll = async () => {
        const result = await MySwal.fire({
            title: "請再次確認",
            text: "將從 Hostify 同步所有房源資料，覆蓋 Hostify 欄位但保留本機欄位，確定執行嗎？",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#61cbc9",
            cancelButtonColor: "#d33",
            confirmButtonText: "確定",
            cancelButtonText: "取消"
        });
        if (!result.isConfirmed) return;

        try {
            await syncAllListings();
            // POST 不回傳 JobId，改打 current 拿
            const currentJob = await getSyncCurrentJob();
            const jobId = currentJob?.JobId;
            setSyncJobId(jobId);
            setSyncStatus('running');
            setSyncDetail(currentJob);
            localStorage.setItem('syncJobId', jobId);
            localStorage.setItem('syncStatus', 'running');
            localStorage.setItem('syncDetail', JSON.stringify(currentJob));
            if (jobId) startPolling(jobId);
        } catch (error) {
            if (error?.response?.status === 409) {

                // 已有 job 在跑，改去拿 current job
                try {
                    const currentJob = await getSyncCurrentJob();
                    const jobId = currentJob?.JobId;
                    setSyncJobId(jobId);
                    setSyncStatus('running');
                    setSyncDetail(currentJob);
                    localStorage.setItem('syncJobId', jobId);
                    localStorage.setItem('syncStatus', 'running');
                    localStorage.setItem('syncDetail', JSON.stringify(currentJob));
                    if (jobId) startPolling(jobId);
                    MySwal.fire("提醒", `已有 Sync Job 正在執行中，已自動追蹤進度。`, "info");
                } catch (e) {
                    MySwal.fire("錯誤", "無法取得目前執行中的 Job", "error");
                }
            } else {
                const msg = error?.response?.data?.message || error.message;
                MySwal.fire("錯誤", msg, "error");
            }
        }
    };

    // 手動查詢 jobId
    const handleQueryJobId = async () => {
        if (!inputJobId) return;
        try {
            const data = await getSyncJobById(inputJobId);
            const status = data?.Status?.toLowerCase();
            setSyncJobId(inputJobId);           // ← 查完才更新 syncJobId
            setSyncDetail(data);
            setSyncStatus(status);
            localStorage.setItem('syncJobId', inputJobId);
            localStorage.setItem('syncDetail', JSON.stringify(data));
            localStorage.setItem('syncStatus', status);
            if (status === 'running') {
                startPolling(inputJobId);
            } else {
                stopPolling();
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err.message;
            MySwal.fire("錯誤", `查詢失敗：${msg}`, "error");
        }
    };

    // 🎯 根據權限檢查結果渲染內容
    if (!hasAccess) {
        return (
            <Wrapper>
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: '#ffe6e6',
                    borderRadius: '8px',
                    border: '2px solid #ff6b6b'
                }}>
                    <h2 style={{ color: '#d63031', marginBottom: '16px' }}>🚫 所有權限檢查都失敗</h2>
                    <p style={{ color: '#2d3436', fontSize: '16px', marginBottom: '8px' }}>
                        無法通過任何權限驗證方式
                    </p>
                    <p style={{ color: '#636e72', fontSize: '14px', marginBottom: '20px' }}>
                        請檢查 Console 的詳細錯誤訊息或嘗試重新登入
                    </p>

                    <button
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                        onClick={handleDebug}
                    >
                        輸出調試資訊到 Console
                    </button>
                </div>
            </Wrapper>
        );
    }

    // 有權限，顯示正常介面
    return (
        <Wrapper>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            {/* <div style={{
                backgroundColor: '#d4edda',
                padding: '15px',
                marginBottom: '20px',
                borderRadius: '8px',
                border: '1px solid #c3e6cb'
            }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#155724' }}>✅ 權限驗證通過</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#155724' }}>
                    驗證方式：{accessMethod}
                </p>
            </div> */}

            <h2>Admin Attention</h2>
            <p>這些操作將影響整個資料庫，請務必謹慎！</p>

            <ButtonSection>
                <div>
                    <SectionTitle>Danger Zone</SectionTitle>
                    <ButtonGrid>
                        {deleteActions.map((action, index) => (
                            <ActionButton
                                key={index}
                                type={action.type}
                                onClick={() => handleAction(action)}
                            >
                                {action.label}
                            </ActionButton>
                        ))}
                    </ButtonGrid>
                </div>

                <div>
                    <SectionTitle>初始化</SectionTitle>
                    <ButtonGrid>
                        {initActions.map((action, index) => (
                            <ActionButton
                                key={index}
                                type={action.type}
                                onClick={() => handleAction(action)}
                            >
                                {action.label}
                            </ActionButton>
                        ))}
                    </ButtonGrid>
                </div>

                <div>
                    <SectionTitle>Debug 功能</SectionTitle>
                    <ButtonGrid>
                        {debugActions.map((action, index) => (
                            <ActionButton
                                key={index}
                                type={action.type}
                                onClick={() => handleAction(action)}
                            >
                                {action.label}
                            </ActionButton>
                        ))}
                    </ButtonGrid>
                </div>

                <div>
                    <SectionTitle>Sync 功能</SectionTitle>
                    <ButtonGrid>
                        <ActionButton type="init" onClick={handleSyncAll}>
                            同步所有房源資料 Sync-All
                        </ActionButton>

                        {/* 目前 Job ID 顯示 + 一鍵複製 */}
                        {syncJobId && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '8px 12px', backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6', borderRadius: '6px',
                                fontSize: '12px', color: '#495057'
                            }}>
                                <span style={{ color: '#868e96' }}>目前 Job ID：</span>
                                <code style={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                    {syncJobId}
                                </code>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(syncJobId);
                                        MySwal.fire({ title: "已複製！", icon: "success", timer: 1000, showConfirmButton: false });
                                    }}
                                    style={{
                                        padding: '3px 8px', fontSize: '11px',
                                        backgroundColor: '#6c757d', color: 'white',
                                        border: 'none', borderRadius: '4px', cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Copy
                                </button>
                            </div>
                        )}

                        {/* Job ID 輸入框 + 查詢按鈕 */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="輸入 Job ID 查詢狀態"
                                value={inputJobId}
                                onChange={(e) => setInputJobId(e.target.value)}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    minWidth: '200px'
                                }}
                            />
                            <button
                                onClick={handleQueryJobId}
                                disabled={!inputJobId}
                                style={{
                                    padding: '8px 14px', fontSize: '13px',
                                    backgroundColor: inputJobId ? '#61cbc9' : '#ccc',
                                    color: 'white', border: 'none',
                                    borderRadius: '6px',
                                    cursor: inputJobId ? 'pointer' : 'not-allowed'
                                }}
                            >
                                查詢狀態
                            </button>
                        </div>

                        {/* 狀態指示器 */}
                        {syncStatus === 'running' && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 14px', backgroundColor: '#fff8e1',
                                border: '1px solid #ffe082', borderRadius: '8px',
                                fontSize: '13px', color: '#795548'
                            }}>
                                <span style={{
                                    display: 'inline-block', width: '14px', height: '14px',
                                    border: '2px solid #61cbc9', borderTopColor: 'transparent',
                                    borderRadius: '50%', animation: 'spin 1s linear infinite'
                                }} />
                                {syncDetail?.Message || 'Syncing...'}
                            </div>
                        )}

                        {syncStatus === 'completed' && (
                            <div style={{
                                padding: '10px 14px', backgroundColor: '#d4edda',
                                border: '1px solid #c3e6cb', borderRadius: '8px',
                                fontSize: '13px', color: '#155724'
                            }}>
                                同步完成！
                                {syncDetail && (
                                    <div style={{ marginTop: '6px', fontSize: '12px' }}>
                                        總筆數：{syncDetail.TotalCount ?? '-'} 筆
                                        {syncDetail.DurationSeconds != null && ` ／ 耗時：${syncDetail.DurationSeconds} 秒`}
                                        {syncDetail.Message && <div style={{ marginTop: '4px' }}>{syncDetail.Message}</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        {syncStatus === 'failed' && (
                            <div style={{
                                padding: '10px 14px', backgroundColor: '#ffe6e6',
                                border: '1px solid #ff6b6b', borderRadius: '8px',
                                fontSize: '13px', color: '#d63031'
                            }}>
                                ❌ 同步失敗：{syncDetail?.Error || syncDetail?.Message || '請查看 Console'}
                            </div>
                        )}
                    </ButtonGrid>
                </div>

                <div>
                    <SectionTitle>匯入功能</SectionTitle>
                    <ButtonGrid>
                        <p style={{ margin: 0, fontSize: '13px', color: '#636e72', fontWeight: 'normal' }}>
                            支援格式：Airbnb、Expedia、Agoda、VRBO 工作表<br />
                            <span style={{ color: '#e17055' }}>⚠ Booking / Marriott 目前不支援</span>
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            onChange={(e) => setImportFile(e.target.files[0] || null)}
                            style={{ fontSize: '14px' }}
                        />
                        {importFile && (
                            <p style={{ margin: 0, fontSize: '13px', color: '#2d3436' }}>
                                已選擇：{importFile.name}
                            </p>
                        )}
                        <ActionButton
                            type="init"
                            onClick={handleImportPlatformUrls}
                            disabled={importing || !importFile}
                            style={{ opacity: (importing || !importFile) ? 0.5 : 1, cursor: (importing || !importFile) ? 'not-allowed' : 'pointer' }}
                        >
                            {importing ? "匯入中..." : "匯入 Listing PlatformUrls"}
                        </ActionButton>
                    </ButtonGrid>
                </div>
            </ButtonSection>

            {/* 🔧 調試按鈕 */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                    onClick={handleDebug}
                >
                    🔍 調試資訊
                </button>
            </div>
        </Wrapper>
    );
};

export default AdminAttention;